<template>
  <div class="main row">
    <div class="col-md-12">
      <KTCodePreview v-bind:title="'Import ngân sách từ Excel'">
        <template v-slot:preview>
          <b-row class="mb-3">
            <b-col>
              <div class="form-group mb-2">
                <span>
                  <b>Bước 1:</b> Vui lòng tải template Excel đúng format ở
                  <a :href="urlExcel">đây</a>
                </span>
              </div>
              <span class="d-block mb-2">
                <b>Bước 2:</b> Upload file Excel để hệ thống đọc & xem trước
              </span>
              <UploadFileMulter
                v-on:send-file="onTransformedExcel"
                v-on:clear-file="onClearExcel"
                :cmdUrl="'v2/budget-lines/transform-import-excel'"
              />
              <span v-if="fileName" class="d-block mt-2"
                >Tên file: <b>{{ fileName }}</b></span
              >
            </b-col>
          </b-row>

          <b-row v-if="previewItems.length" class="mb-4">
            <b-col>
              <h5>Xem trước dữ liệu ({{ previewItems.length }} dòng)</h5>
              <b-alert
                v-if="previewErrorCount"
                show
                variant="warning"
                class="mb-3"
              >
                Có {{ previewErrorCount }} dòng lỗi/không map được dữ liệu từ
                Excel (sẽ được bỏ qua khi import).
              </b-alert>
              <b-table
                :items="pagedPreviewItems"
                :fields="previewFields"
                bordered
                hover
                small
              />
              <div
                v-if="previewItems.length > previewPerPage"
                class="d-flex align-items-center justify-content-between mt-2 custom-pagination"
              >
                <div>
                  Hiển thị {{ previewStartRow }}-{{ previewEndRow }} /
                  {{ previewItems.length }} dòng
                </div>
                <b-pagination
                  v-model="previewCurrentPage"
                  :total-rows="previewItems.length"
                  :per-page="previewPerPage"
                  size="sm"
                  align="right"
                  class="mb-0"
                />
              </div>
            </b-col>
          </b-row>

          <b-row class="mb-4">
            <b-col cols="8">
              <b-card class="mb-0">
                <h6 class="mb-3">Quy trình duyệt ngân sách</h6>
                <div v-if="approvalSteps.length">
                  <b-row
                    v-for="(step, index) in approvalSteps"
                    :key="`approval-step-${step.step}-${index}`"
                    class="mb-2"
                  >
                    <b-col cols="4" class="d-flex align-items-center">
                      <strong>Bước {{ step.step }}</strong>
                    </b-col>
                    <b-col cols="8">
                      <b-form-select
                        v-model.number="step.approverBy"
                        :options="step.ownerOptions"
                      />
                    </b-col>
                  </b-row>
                </div>
                <div v-else class="text-muted">
                  Chưa có cấu hình duyệt cho ngân sách.
                </div>
              </b-card>
            </b-col>
          </b-row>

          <b-row>
            <b-col>
              <b-button
                variant="primary"
                :disabled="!previewItems.length || isSaving"
                @click="onImport"
              >
                <i class="fas fa-upload"></i>
                {{ isSaving ? 'Đang import...' : 'Import' }}
              </b-button>
              <b-button variant="secondary" class="ml-2" @click="goBack">
                <i class="fas fa-arrow-left"></i> Quay lại
              </b-button>
            </b-col>
          </b-row>

          <b-row v-if="importResult" class="mt-4">
            <b-col>
              <b-alert show variant="success">
                Import thành công {{ importResult.total }} dòng.
              </b-alert>
              <div v-if="importResult?.results?.length" class="text-muted mt-2">
                Tạo mới: {{ importCreatedCount }}, Cập nhật:
                {{ importUpdatedCount }}
                <span v-if="importFailedCount"
                  >, Lỗi: {{ importFailedCount }}</span
                >
              </div>
            </b-col>
          </b-row>
        </template>
      </KTCodePreview>
    </div>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ApiService from '@/core/services/api.service';
import { makeToastSuccess, makeToastFaile, handleError } from '@/utils/common';
import { mergeApprovalConfigDetailsByStep } from '@/utils/approval-config';
import UploadFileMulter from '@/view/modules/upload/UploadFileMulter.vue';
import { BASE_URL } from '@/utils/constants';
import { APPROVAL_CONFIG_TYPE } from '@/utils/enum';

export default {
  components: { KTCodePreview, UploadFileMulter },
  data() {
    return {
      urlExcel: BASE_URL + 'templates/download?type=budget-line-import',
      fileName: '',
      companyId: null,
      previewItems: [],
      previewPerPage: 20,
      previewCurrentPage: 1,
      isSaving: false,
      importResult: null,
      approvalType: APPROVAL_CONFIG_TYPE.BUDGET,
      approvalSteps: [],
      departmentNameToId: {},
      costItemNameToId: {},
      storeNameToId: {},
      previewFields: [
        { key: 'departmentName', label: 'Phòng ban' },
        { key: 'storeName', label: 'Địa điểm' },
        { key: 'costItemName', label: 'Khoản mục chi phí' },
        { key: 'year', label: 'Năm' },
        { key: 'month', label: 'Tháng' },
        { key: 'description', label: 'Diễn giải' },
        { key: 'allocatedAmount', label: 'Phân bổ' },
        { key: 'usedAmount', label: 'Đã dùng' },
        { key: 'excelRemainingAmount', label: 'Còn lại (Excel)' },
        { key: 'computedRemainingAmount', label: 'Còn lại (Hệ thống)' },
        { key: 'errors', label: 'Lỗi mapping' },
      ],
    };
  },
  computed: {
    sortedPreviewItems() {
      return [...(this.previewItems || [])].sort((a, b) => {
        const aHasError = (a.errorsList || []).length > 0 ? 0 : 1;
        const bHasError = (b.errorsList || []).length > 0 ? 0 : 1;
        return aHasError - bHasError;
      });
    },
    pagedPreviewItems() {
      const start = (this.previewCurrentPage - 1) * this.previewPerPage;
      return this.sortedPreviewItems.slice(start, start + this.previewPerPage);
    },
    previewStartRow() {
      if (!this.previewItems.length) return 0;
      return (this.previewCurrentPage - 1) * this.previewPerPage + 1;
    },
    previewEndRow() {
      return Math.min(
        this.previewCurrentPage * this.previewPerPage,
        this.previewItems.length,
      );
    },
    previewErrorCount() {
      return (this.previewItems || []).reduce((sum, i) => {
        const errs = i.errorsList || [];
        return sum + (errs.length ? 1 : 0);
      }, 0);
    },
    importCreatedCount() {
      const results = this.importResult?.results || [];
      return results.filter((r) => r.action === 'created').length;
    },
    importUpdatedCount() {
      const results = this.importResult?.results || [];
      return results.filter((r) => r.action === 'updated').length;
    },
    importFailedCount() {
      const results = this.importResult?.results || [];
      return results.filter((r) => r.action === 'failed').length;
    },
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Kế toán', route: '#' },
      { title: 'Ngân sách', route: '/budget/budget-lines' },
      { title: 'Import Excel' },
    ]);
    this.fetchApprovalConfig();
    this.fetchLookupMaps();
  },
  methods: {
    makeToastSuccess,
    makeToastFaile,
    onTransformedExcel(payload, fileName) {
      this.fileName = fileName || '';
      const rows = payload?.previewItems || payload?.rows || [];
      const normalized = (rows || []).map((r) => {
        const errorsList = r?.errorsList || r?.errors || [];
        return {
          ...r,
          errorsList,
          errors: Array.isArray(errorsList) ? errorsList.join('; ') : String(errorsList || ''),
        };
      });
      this.previewItems = normalized;
      const firstCompanyId = normalized.find((r) => r?.companyId)?.companyId;
      this.companyId =
        firstCompanyId !== undefined && firstCompanyId !== null
          ? Number(firstCompanyId) || null
          : null;
      this.previewCurrentPage = 1;
      this.importResult = null;
    },
    onClearExcel() {
      this.fileName = '';
      this.companyId = null;
      this.previewItems = [];
      this.previewCurrentPage = 1;
      this.importResult = null;
    },
    normalizeKey(value) {
      if (value === null || value === undefined) return '';
      return String(value)
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
    },
    toNumber(value) {
      if (value === null || value === undefined) return null;
      if (typeof value === 'number' && !Number.isNaN(value)) return value;
      const str = String(value).trim();
      if (!str) return null;
      // keep digits only (Excel may contain separators like "," "." spaces)
      const cleaned = str.replace(/[^\d-]/g, '');
      if (!cleaned || cleaned === '-') return null;
      const n = Number(cleaned);
      return Number.isFinite(n) ? n : null;
    },
    parseMonth(value) {
      const n = this.toNumber(value);
      if (n !== null) return n >= 1 && n <= 12 ? n : null;
      const str = String(value || '').trim();
      const m = str.match(/(\d{1,2})/i);
      const month = m ? Number(m[1]) : null;
      return month && month >= 1 && month <= 12 ? month : null;
    },
    async fetchLookupMaps() {
      try {
        const [depRes, costRes, storeRes] = await Promise.all([
          ApiService.query('departments/all'),
          ApiService.get('v2/cost-items/all'),
          ApiService.query('stores/getStores'),
        ]);

        const deps = depRes?.data?.data || [];
        const costs = costRes?.data?.data || [];
        const stores = storeRes?.data?.data || [];

        const depMap = {};
        deps.forEach((d) => {
          if (d?.name) depMap[this.normalizeKey(d.name)] = Number(d.id);
        });
        const costMap = {};
        costs.forEach((c) => {
          if (c?.name) costMap[this.normalizeKey(c.name)] = Number(c.id);
        });

        const storeMap = {};
        stores.forEach((s) => {
          if (!s) return;
          if (s.name) storeMap[this.normalizeKey(s.name)] = Number(s.id);
          if (s.shortName)
            storeMap[this.normalizeKey(s.shortName)] = Number(s.id);
          // also map "name (short)"
          if (s.shortName && s.name) {
            storeMap[this.normalizeKey(`${s.name} (${s.shortName})`)] = Number(
              s.id,
            );
          }
        });

        this.departmentNameToId = depMap;
        this.costItemNameToId = costMap;
        this.storeNameToId = storeMap;
      } catch (e) {
        // Best-effort: keep empty maps so numeric *_ID columns still work
        this.departmentNameToId = {};
        this.costItemNameToId = {};
        this.storeNameToId = {};
      }
    },
    mapApprovalSteps(details) {
      const merged = mergeApprovalConfigDetailsByStep(details || []);
      this.approvalSteps = merged.map((detail) => {
        const ownerMap = {};
        const owners = [];
        (detail.owners || []).forEach((owner) => {
          if (!owner || !owner.id || ownerMap[owner.id]) return;
          ownerMap[owner.id] = true;
          owners.push(owner);
        });
        return {
          step: detail.step,
          approverBy: null,
          ownerOptions: [
            { value: null, text: `-- Chọn người duyệt bước ${detail.step} --` },
            ...owners.map((owner) => ({
              value: Number(owner.id),
              text: owner.name ? `${owner.name} (${owner.id})` : `${owner.id}`,
            })),
          ],
        };
      });
    },
    fetchApprovalConfig() {
      ApiService.get(`v2/approval-configs/by-type/${this.approvalType}`)
        .then(({ data }) => {
          if (data.status === 200 && data.data) {
            this.mapApprovalSteps(data.data.details || []);
          } else {
            this.approvalSteps = [];
          }
        })
        .catch(() => {
          this.approvalSteps = [];
        });
    },
    onImport() {
      if (!this.approvalSteps.length) {
        this.makeToastFaile('Chưa có cấu hình quy trình duyệt ngân sách!');
        return;
      }
      const invalidStep = this.approvalSteps.find((step) => !step.approverBy);
      if (invalidStep) {
        this.makeToastFaile(
          `Vui lòng chọn người duyệt cho bước ${invalidStep.step}!`,
        );
        return;
      }

      const validItems = (this.previewItems || []).filter(
        (i) =>
          (i.errorsList || []).length === 0 && i.departmentId && i.costItemId,
      );
      const skippedCount = (this.previewItems || []).length - validItems.length;
      if (!validItems.length) {
        this.makeToastFaile(
          'Không có dòng hợp lệ để import. Kiểm tra lại file Excel!',
        );
        return;
      }
      if (skippedCount > 0) {
        this.makeToastFaile(
          `Bỏ qua ${skippedCount} dòng lỗi/không map được. Chỉ import ${validItems.length} dòng hợp lệ.`,
        );
      }

      this.isSaving = true;
      const payload = {
        items: validItems.map((i) => ({
          departmentId: i.departmentId,
          costItemId: i.costItemId,
          storeId: i.storeId,
          year: i.year,
          month: i.month,
          description: i.description || '',
          allocatedAmount: i.allocatedAmount,
          usedAmount: i.usedAmount,
        })),
        companyId: this.companyId,
        approvalType: this.approvalType,
        approvers: this.approvalSteps
          .map((step) => ({
            step: step.step,
            approverBy: step.approverBy,
          }))
          .sort((a, b) => a.step - b.step),
      };
      ApiService.post('v2/budget-lines/import', payload)
        .then(({ data }) => {
          if (data.status === 200) {
            this.importResult = data.data;
            this.makeToastSuccess(data.message);
            this.$router.push({ name: 'list-budget-lines' }).catch(() => {});
          } else {
            this.makeToastFaile(data.message);
          }
        })
        .catch((err) => handleError(err))
        .finally(() => {
          this.isSaving = false;
        });
    },
    goBack() {
      this.$router.push({ name: 'list-budget-lines' });
    },
  },
};
</script>
